import { IonIcon } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { helpCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { ApplicationListItem } from "@/components/molecules/application/application-list-item";
import { InfoModal } from "@/components/molecules/info-modal";
import type { Application } from "@/lib/backend/api";
import { useRevokePeriod } from "@/lib/backend/utils/hooks/use-revoke-period";
import { useApplicationInfoSections } from "@/lib/constants/application-info";
import { getSignupTitle } from "@/lib/utils/scheduling-text";

interface ApplicationsModalProps {
  /** Modal reference for controlling open/close */
  modalRef: React.RefObject<HTMLIonModalElement>;
  /** Array of applications to display */
  applications: Application[];
  /** Optional callback when user wants to cancel/unregister from an application */
  onCancelApplication?: (applicationId: string) => void;
  /** Scheduling method to determine terminology */
  schedulingMethod?: string;
}

/**
 * Modal displaying all user applications for a project. Shows application status, shift details,
 * and timing information.
 */
export function ApplicationsModal({
  modalRef,
  applications,
  onCancelApplication,
  schedulingMethod,
}: ApplicationsModalProps) {
  const { t } = useTranslation();
  const [applicationToRevoke, setApplicationToRevoke] = useState<Application | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const confirmationModalRef = useRef<HTMLIonModalElement>(null);
  const infoModalRef = useRef<HTMLIonModalElement>(null);
  const { canRevokeApplication, getRevokePeriodText, revokePeriod, revokeNotice } =
    useRevokePeriod();
  const applicationInfoSections = useApplicationInfoSections(
    getRevokePeriodText,
    revokePeriod,
    schedulingMethod,
  );

  // Present confirmation modal after applicationToRevoke is set
  useEffect(() => {
    if (applicationToRevoke) {
      confirmationModalRef.current?.present();
    }
  }, [applicationToRevoke]);

  // Reset state when modal is dismissed
  useEffect(() => {
    const modal = modalRef.current;
    const handleDidDismiss = () => {
      setApplicationToRevoke(null);
      setResetKey((prev) => prev + 1);
    };

    modal?.addEventListener("ionModalDidDismiss", handleDidDismiss);
    return () => {
      modal?.removeEventListener("ionModalDidDismiss", handleDidDismiss);
    };
  }, [modalRef]);

  return (
    <ModalSheetAutoHeight ref={modalRef}>
      <div className="flex max-h-[80vh] flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between">
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-txt-primary">Aanmeldingen</h2>
            <p className="mt-1 text-sm text-txt-secondary">
              {t("common:applications.count", { count: applications.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => infoModalRef.current?.present()}
            className="flex h-8 w-8 items-center justify-center"
          >
            <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
          </button>
        </div>

        {/* Applications list - scrollable */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          {applications.map((application) => {
            // Check if this application can be revoked based on the revoke period
            const canRevoke = canRevokeApplication(application.startAt);
            // Only show three dots menu for PENDING or ACCEPTED applications that can be revoked
            const showMenu =
              canRevoke && (application.status === "PENDING" || application.status === "ACCEPTED");

            return (
              <ApplicationListItem
                key={`${application.id}-${resetKey}`}
                application={application}
                showMenu={showMenu}
                onRevokeClick={() => {
                  setApplicationToRevoke(application);
                }}
              />
            );
          })}
        </div>

        {/* Bij `NOT_ALLOWED` verdwijnt het driepuntsmenu van elke regel hierboven. Eén zin onder de
            lijst legt dat uit — onder de lijst en niet per regel, want dezelfde mededeling vijf keer
            herhaald is ruis en het geldt voor alle aanmeldingen tegelijk. */}
        {revokeNotice && (
          <p className="shrink-0 text-sm leading-relaxed text-txt-secondary">{revokeNotice}</p>
        )}
      </div>

      {/* Info Modal */}
      <InfoModal
        modalRef={infoModalRef}
        title={getSignupTitle(t, schedulingMethod)}
        sections={applicationInfoSections}
      />

      {/* Confirmation Modal */}
      <ModalSheetAutoHeight ref={confirmationModalRef}>
        <div className="flex flex-col gap-6 p-6">
          {/* Header */}
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-txt-primary">Aanmelding intrekken</h2>
          </div>

          {/* Content */}
          <p className="text-sm leading-relaxed text-txt-secondary">
            Weet je zeker dat je je aanmelding wilt intrekken? Deze actie kan niet ongedaan worden
            gemaakt.
          </p>

          {/* Action button */}
          <button
            type="button"
            onClick={() => {
              if (onCancelApplication && applicationToRevoke) {
                onCancelApplication(applicationToRevoke.id);
              }
              confirmationModalRef.current?.dismiss();
              modalRef.current?.dismiss();
              setApplicationToRevoke(null);
            }}
            className="w-full rounded-lg bg-error-100 py-3 text-center text-base font-semibold text-white"
          >
            Ja, aanmelding intrekken
          </button>
        </div>
      </ModalSheetAutoHeight>
    </ModalSheetAutoHeight>
  );
}
